import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { Addresses } from "../customer/addresses";
import { Aws } from "../aws/aws";
import { CameraProvider } from 'src/shared/providers/camera/camera';
import { CustomNavParams } from 'src/shared/providers/custom-nav-params/custom-nav-params';
import { Instructions } from "../instructions/instructions";
import { environment } from 'src/environments/environment';

export type PhotoNoteType = 'access' | 'closing' | 'group' | 'parking' | 'room' | 'task' | 'room_object' | 'address' | 'guest_access' | 'guest_parking';

export interface PhotoNotePayload {
  type: PhotoNoteType;
  object_id: number;
  photo_url: string;
  caption?: string;
  s3_photo_url_key?: string;
}

export interface PhotoNote {
  id: number;
  type: PhotoNoteType;
  object_id: number;
  photo_url: string;
  caption?: string;
  photo_note_caption?: string;
  created_at?: string;
  updated_at?: string;
}

@Injectable()
export class PhotoNotes {

  addressId: number;
  private readonly baseUrl = `${environment.api_url}api/v1/customer/photo-notes`;

  constructor(
    private addresses: Addresses,
    private aws: Aws,
    private camera: CameraProvider,
    private customNavParams: CustomNavParams,
    private http: HttpClient,
    private instructions: Instructions,
  ) {
  }

  async captureUploadPhoto(photoKey) {
    const picture = await this.getImg();
    return await this.uploadPictureToS3(picture.dataUrl, photoKey);
  }

  async addPhotoNote(type, params) {
    if (type === 'task') {
      return await this.taskPhotoNotes(params.roomId, params.taskId);
    }

    if (type === 'room') {
      return await this.roomPhotoNote(params.roomId, '');
    }

    return await this.addressPhotoNote(type);
  }

  async addressPhotoNote(type) {
    const awsResp = await this.captureUploadPhoto(`${type}`);
    const addressId = parseInt(localStorage.getItem('addressId')) || this.addressId;
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    const payload = {
      photo_notes: [{
        type: 'address',
        object_id: addressId,
        photo_url: awsResp.Location,
        s3_photo_url_key: awsResp.Key
      }]
    };
    return firstValueFrom(this.http.post(url, payload));
  }

  // Pages that edit a property other than the current one pass its id explicitly.
  async saveAddressPhotoNote(type: PhotoNoteType, s3PhotoUrlKey: string, addressId?: number) {
    addressId = addressId || parseInt(localStorage.getItem('addressId')) || this.addressId;
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/photo-notes`;
    return firstValueFrom(this.http.post(url, { s3_photo_url_key: s3PhotoUrlKey, type }));
  }

  async roomPhotoNote(card: any, awsKey: string, photoUrl?: string) {
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    const payload = {
      photo_notes: [{
        type: card.type === 'group' ? 'room_object' : 'room',
        object_id: card.id,
        photo_url: photoUrl || awsKey,
        s3_photo_url_key: awsKey
      }]
    };
    return firstValueFrom(this.http.post(url, payload));
  }

  async taskPhotoNotes(roomId: any, taskId: any) {
    const photoKey = `${roomId}/${taskId}`;
    const awsResp = await this.captureUploadPhoto(photoKey);
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    const payload = {
      photo_notes: [{
        type: 'task',
        object_id: taskId,
        photo_url: awsResp.Location,
        s3_photo_url_key: awsResp.Key
      }]
    };
    return firstValueFrom(this.http.post(url, payload));
  }

  async photoCaption(photoNoteId: number, caption: string) {
    const url = `${environment.api_url}api/v1/customer/photo-notes/${photoNoteId}`;
    return firstValueFrom(this.http.put(url, { caption }));
  }

  async deletePhotoNotes(photoNoteId: number) {
    const url = `${this.baseUrl}/${photoNoteId}`;
    return firstValueFrom(this.http.delete(url));
  }

  async createPhotoNotes(photoNotes: PhotoNotePayload[]): Promise<PhotoNote[]> {
    const payload = { photo_notes: photoNotes };
    return firstValueFrom(this.http.post<PhotoNote[]>(this.baseUrl, payload));
  }

  async createPhotoNote(type: PhotoNoteType, objectId: number, photoUrl: string, caption?: string, s3PhotoUrlKey?: string): Promise<PhotoNote[]> {
    const photoNote: PhotoNotePayload = {
      type,
      object_id: objectId,
      photo_url: photoUrl
    };
    if (caption) {
      photoNote.caption = caption;
    }
    if (s3PhotoUrlKey) {
      photoNote.s3_photo_url_key = s3PhotoUrlKey;
    }
    return this.createPhotoNotes([photoNote]);
  }

  async createGroupPhotoNote(groupId: number, photoUrl: string, caption?: string): Promise<PhotoNote[]> {
    return this.createPhotoNote('group', groupId, photoUrl, caption);
  }

  async createAccessPhotoNote(addressId: number, photoUrl: string, caption?: string, s3PhotoUrlKey?: string): Promise<PhotoNote[]> {
    return this.createPhotoNote('access', addressId, photoUrl, caption, s3PhotoUrlKey);
  }

  async createClosingPhotoNote(addressId: number, photoUrl: string, caption?: string, s3PhotoUrlKey?: string): Promise<PhotoNote[]> {
    return this.createPhotoNote('closing', addressId, photoUrl, caption, s3PhotoUrlKey);
  }

  async createParkingPhotoNote(addressId: number, photoUrl: string, caption?: string, s3PhotoUrlKey?: string): Promise<PhotoNote[]> {
    return this.createPhotoNote('parking', addressId, photoUrl, caption, s3PhotoUrlKey);
  }

  async createTaskPhotoNote(taskId: number, photoUrl: string, caption?: string, s3PhotoUrlKey?: string): Promise<PhotoNote[]> {
    return this.createPhotoNote('task', taskId, photoUrl, caption, s3PhotoUrlKey);
  }

  async createRoomObjectPhotoNote(roomObjectId: number, photoUrl: string, caption?: string, s3PhotoUrlKey?: string): Promise<PhotoNote[]> {
    return this.createPhotoNote('room_object', roomObjectId, photoUrl, caption, s3PhotoUrlKey);
  }

  async updatePhotoNote(photoNoteId: number, caption: string): Promise<PhotoNote> {
    const url = `${this.baseUrl}/${photoNoteId}`;
    return firstValueFrom(this.http.put<PhotoNote>(url, { caption }));
  }

  async deletePhotoNote(photoNoteId: number): Promise<void> {
    const url = `${this.baseUrl}/${photoNoteId}`;
    return firstValueFrom(this.http.delete<void>(url));
  }

  splitAddressPhotos(addressPhotos: any) {
    const splitedPhotos = {
      parking: [],
      access: [],
      closing: []
    }
    addressPhotos.forEach(item => {
      splitedPhotos[item.type].push(item);
    });

    return splitedPhotos;
  }

  async getImg() {
    return await this.camera.getImg();
  }

  async uploadPictureToS3(picture: string, key: any) {
    const addressId = parseInt(localStorage.getItem('addressId')) || this.addressId ;
    const uniqueKey = `${addressId}/${key}/${Date.now()}`;
    return await this.aws.uploadImageToS3(picture, uniqueKey);
  }

  async getPhotos(type: any, params: any = null) {
    if (type === 'task') {
      return await this.getTodosPhotos(params);
    }

    if (type === 'room') {
      return await this.getRoomPhotos(params);
    }

    return await this.getAddressPhotos(type);
  }

  async getTodosPhotos(taskParams: any) {
    const room = await this.instructions.getRoom(taskParams);
    const updatedTask = room.tasks.find(item => item.id === taskParams.taskId);

    return updatedTask.photo_notes;
  }

  async getRoomPhotos(params: any) {
    const room = await this.instructions.getRoom(params);
    return room.room.photo_notes;
  }

  async getAddressPhotos(type: any) {
    const addressInfo = await this.addresses.getAddress();
    const addressPhotos = this.splitAddressPhotos(addressInfo.photo_notes);
    return addressPhotos[type];
  }

  handleDesktop(navCtrl: any) {
    const desktopPageContent = {
      title: 'Photo Note',
      description: 'Please use the iOS or Android app on your mobile phone to add a photo note.',
      buttonText: 'Go Back'
    };

    this.customNavParams.setParams('info-page', desktopPageContent)
    navCtrl.navigateForward('info-page');
  }

  handleError(navCtrl: any) {
    const errorPageContent = {
      title: 'Photo Note',
      description: 'Something went wrong. Please Try again',
      buttonText: 'Go Back'
    };

    this.customNavParams.setParams('info-page', errorPageContent)
    navCtrl.navigateForward('info-page');
  }

  deletePhotoAlert() {
    const deletePhotoAlert = {
      header: 'Delete Photo?',
      message: '',
      buttons: [
        {
          text: 'Go back',
          role: 'cancel',
          handler: null
        },
        {
          text: 'Delete'
        }
      ]
    };

    return deletePhotoAlert;
  }

  async addTempPhoto(picture = null) {
    const addressId = parseInt(localStorage.getItem('addressId')) || this.addressId ;
    picture = await this.getImg();
    const awsResponse = await this.uploadPictureToS3(picture, addressId);

    const data = {...awsResponse, photo_url: picture};
    return data;
  }

}
